import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import BgColorContainer from '../../Layout/BgColorContainer';
import ScrollWords from '../../ui/cinematic/ScrollWords';
import { entradaCrema } from '../../ui/cinematic/Bloque';
import useReveal from '../../../hooks/useReveal';
import useCursorZone from '../../../hooks/useCursorZone';
import useScrollMarquee from '../../../hooks/useScrollMarquee';
import { CLASE_LOGO, useClientes } from '../../../cms/clientes';
import { urlImagen } from '../../../cms/sanity';
import { elemento, partes } from '../../../cms/textos';

gsap.registerPlugin(ScrollTrigger, useGSAP);

// Una fila tiene que ser más ancha que la pantalla para que el loop no deje un
// hueco: con pocos logos (al repartirlos en tres) se repiten hasta este mínimo
const MINIMO_POR_FILA = 8;

/** Una fila infinita: el contenido va duplicado y GSAP desplaza el track -50%. */
function FilaLogos({ clientes }) {
    const vueltas = Math.ceil(MINIMO_POR_FILA / clientes.length);
    const base = Array.from({ length: vueltas }, () => clientes).flat();
    return (
        <div className="marquee-fade overflow-hidden">
            <div className="clientes-track flex w-max will-change-transform">
                {[...base, ...base].map((cliente, i) => (
                    <div key={`${cliente.id}-${i}`} className="flex h-20 w-48 shrink-0 items-center justify-center px-6 md:h-28 md:w-64">
                        <img
                            src={urlImagen(cliente.logo, { ancho: 480 })}
                            alt={i < clientes.length ? cliente.nombre : ''}
                            loading="lazy"
                            className={`max-h-full w-full object-contain opacity-80 transition-opacity duration-500 hover:opacity-100 ${CLASE_LOGO.crema}`}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
}

/** Textos de "Páginas → Inicio" (Home.jsx); los logos, de "Clientes". */
function Clientes({ textos }) {
    const sectionRef = useRef(null);
    const ref = useReveal();
    const cursorZone = useCursorZone('light');
    const Ceja = elemento(textos, 'ceja', 'p');
    const Bajada = elemento(textos, 'bajada', 'p');
    const { clientes } = useClientes();
    // Tres filas: cada una toma un logo de cada tres, así quedan parejas
    const filas = [0, 1, 2].map((f) => clientes.filter((_, i) => i % 3 === f));

    // La tarjeta que se abre a todo el ancho queda solo acá, como firma de la home
    useGSAP(() => {
        entradaCrema(sectionRef.current, 'tarjeta');
    }, { scope: sectionRef });

    // Filas en sentidos alternados que reaccionan a la velocidad del scroll
    useScrollMarquee(sectionRef, '.clientes-track', { dependencias: [clientes.length] });

    return (
        <BgColorContainer>
            <section ref={sectionRef} className="relative overflow-hidden bg-cream py-24 text-ink md:py-32" aria-label="Clientes" {...cursorZone}>
                <div ref={ref} className="container flex flex-col gap-6 px-5 md:px-10 lg:flex-row lg:items-end lg:justify-between 2xl:px-0">
                    <div className="flex max-w-3xl flex-col gap-6">
                        <Ceja data-reveal className="flex items-center gap-4 text-xs uppercase tracking-[0.3em] text-ink/50 md:text-sm">
                            <span>02</span>
                            <span className="h-px w-10 bg-ink/30" />
                            <span>{textos.ceja}</span>
                        </Ceja>
                        <ScrollWords
                            as={elemento(textos, 'titular', 'h2')}
                            className="text-4xl font-medium tracking-tight md:text-6xl"
                            start="top 90%"
                            end="bottom 60%"
                            parts={partes(textos.titular)}
                        />
                    </div>
                    <Bajada data-reveal className="max-w-xs text-base leading-relaxed text-ink/60">
                        {textos.bajada}
                    </Bajada>
                </div>

                <div className="mt-14 flex flex-col gap-2 md:mt-20">
                    {filas.map((fila, i) => fila.length > 0 && <FilaLogos key={i} clientes={fila} />)}
                </div>
            </section>
        </BgColorContainer>
    );
}

export default Clientes;
